import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { delay } from '~/common/utils/async.util';
import { getNoticeDetailApi } from '~/features/notices/api/get-notice-detail.api';
import { getNoticesApi } from '~/features/notices/api/get-notices.api';

import type { Nullable } from '~/common/types/nullable.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  NoticeDetailItem,
  NoticeListItem,
  NoticeListParams,
} from '~/features/notices/types/notices.type';

export interface NoticesState {
  isLoading: boolean;
  isLoadingDetail: boolean;
  items: NoticeListItem[];
  meta: Nullable<PaginationMeta>;
  detail: Nullable<NoticeDetailItem>;
}

const initialState: NoticesState = {
  isLoading: false,
  isLoadingDetail: false,
  items: [],
  meta: null,
  detail: null,
};

/**
 * 공지사항 목록 조회
 */
export const fetchNotices = createAsyncThunk(
  'notices/fetchNotices',
  async (params: NoticeListParams | undefined, { rejectWithValue }) => {
    try {
      const [{ data }] = await Promise.all([getNoticesApi(params), delay(200)]);
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 공지사항 상세 조회
 */
export const fetchNoticeDetail = createAsyncThunk(
  'notices/fetchNoticeDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await getNoticeDetailApi(id);
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const NoticesSlice = createSlice({
  name: 'notices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchNotices.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchNotices.fulfilled, (state, action) => {
        state.items = action.payload.data;
        state.meta = action.payload.meta;
        state.isLoading = false;
      })
      .addCase(fetchNotices.rejected, state => {
        state.isLoading = false;
        state.items = [];
        state.meta = null;
      });

    builder
      .addCase(fetchNoticeDetail.pending, state => {
        state.isLoadingDetail = true;
      })
      .addCase(fetchNoticeDetail.fulfilled, (state, action) => {
        state.detail = action.payload.data;
        state.isLoadingDetail = false;
      })
      .addCase(fetchNoticeDetail.rejected, state => {
        state.detail = null;
        state.isLoadingDetail = false;
      });
  },
});

export default NoticesSlice;
